import type {
	ColumnConfiguration,
	ListBuilderConfiguration,
	MobileCardConfiguration,
} from '../types';

/**
 * Stored customer statements list (PD-14042) — the Sales > Statements page.
 *
 * Backed by GET /api/statements (PD-14041), which serves the DENORMALIZED
 * list row (statementNumber / clientName / asOfDate / period bounds /
 * endingBalance / totalAmountDue / generationSource / distributedDate) and
 * accepts only the narrow query params clientId / companyId / asOfDateStart /
 * asOfDateEnd. The statements module's queryFn translates the filter values
 * into those params, server-side ordering is fixed (newest first), and there
 * is no fulltext search — hence sortable/search are off here.
 */
export const createStatementColumns = (): Array<ColumnConfiguration> => [
	{
		id: 'statement-number',
		fields: [
			{
				id: 'statement-number-field',
				// The statement number is the click target into the detail view.
				// A row-level `clickAction` is NOT wired by the list-builder (only
				// a field-level link renders as a navigable anchor), so without
				// this the blue-styled "ST-…" looked clickable but did nothing
				// (PD-14176). A `custom` urlTemplate is used instead of an
				// `object` link because these statement configs carry no `objects`
				// registry, so an object link would mis-resolve to `/statement/:id`
				// (singular) rather than the real `/statements/:id` route.
				link: {
					type: 'custom',
					enabled: true,
					urlTemplate: '/statements/{id}',
				},
				dataConfig: {
					type: 'field',
					field: { fieldName: 'statementNumber', objectTable: 'statement' },
				},
				styleConfig: {
					size: 'sm',
					colorRule: { type: 'theme', themeColor: 'primary' },
				},
				displayStyle: 'text',
			},
		],
		headers: [{ id: 'h-number', text: 'Statement #' }],
		minWidth: 110,
		sortable: false,
		alignment: 'left',
		widthConfig: { mode: 'flexible', preset: 'small' },
		verticalAlignment: 'top',
	},
	{
		id: 'client-info',
		fields: [
			{
				id: 'client-name-field',
				link: {
					type: 'object',
					enabled: true,
					idField: 'fkClientId',
					objectTable: 'client',
				},
				dataConfig: {
					type: 'field',
					field: { fieldName: 'clientName', objectTable: 'statement' },
				},
				styleConfig: {
					size: 'sm',
					colorRule: { type: 'theme', themeColor: 'primary' },
				},
				displayStyle: 'text',
			},
		],
		headers: [{ id: 'h-client', text: 'Customer' }],
		minWidth: 180,
		sortable: false,
		alignment: 'left',
		widthConfig: { mode: 'flexible', preset: 'large' },
		verticalAlignment: 'top',
	},
	{
		id: 'as-of',
		fields: [
			{
				id: 'as-of-field',
				dataConfig: {
					type: 'field',
					field: { fieldName: 'asOfDate', objectTable: 'statement' },
				},
				styleConfig: {
					size: 'sm',
					colorRule: { type: 'default' },
					formatOptions: { dateFormat: 'short' },
				},
				displayStyle: 'date',
			},
		],
		headers: [{ id: 'h-as-of', text: 'As Of' }],
		minWidth: 90,
		sortable: false,
		alignment: 'left',
		widthConfig: { mode: 'flexible', preset: 'small' },
		verticalAlignment: 'top',
	},
	{
		id: 'period',
		fields: [
			{
				id: 'period-start-field',
				dataConfig: {
					type: 'field',
					field: { fieldName: 'periodStartDate', objectTable: 'statement' },
				},
				styleConfig: {
					size: 'sm',
					colorRule: { type: 'default' },
					formatOptions: { dateFormat: 'short' },
				},
				displayStyle: 'date',
			},
			{
				id: 'period-line-break',
				dataConfig: { type: 'custom_text', customText: '' },
				styleConfig: { size: 'md', colorRule: { type: 'default' } },
				displayStyle: 'line_break',
			},
			{
				id: 'period-end-field',
				dataConfig: {
					type: 'field',
					field: { fieldName: 'periodEndDate', objectTable: 'statement' },
				},
				styleConfig: {
					size: 'sm',
					colorRule: { type: 'theme', themeColor: 'textSecondary' },
					formatOptions: { dateFormat: 'short' },
				},
				displayStyle: 'date',
			},
		],
		headers: [
			{ id: 'h-period-start', text: 'Period Start' },
			{ id: 'h-period-end', text: 'Period End' },
		],
		minWidth: 110,
		sortable: false,
		alignment: 'left',
		widthConfig: { mode: 'flexible', preset: 'medium' },
		verticalAlignment: 'top',
	},
	{
		id: 'ending-balance',
		fields: [
			{
				id: 'ending-balance-field',
				dataConfig: {
					type: 'field',
					field: { fieldName: 'endingBalance', objectTable: 'statement' },
				},
				styleConfig: { size: 'sm', colorRule: { type: 'default' } },
				displayStyle: 'currency',
			},
		],
		headers: [{ id: 'h-ending-balance', text: 'Ending Balance' }],
		minWidth: 110,
		sortable: false,
		alignment: 'right',
		widthConfig: { mode: 'flexible', preset: 'small' },
		verticalAlignment: 'top',
	},
	{
		id: 'amount-due',
		fields: [
			{
				id: 'amount-due-field',
				dataConfig: {
					type: 'field',
					field: { fieldName: 'totalAmountDue', objectTable: 'statement' },
				},
				styleConfig: { size: 'sm', colorRule: { type: 'default' } },
				displayStyle: 'currency',
			},
		],
		headers: [{ id: 'h-amount-due', text: 'Amount Due' }],
		minWidth: 100,
		sortable: false,
		alignment: 'right',
		widthConfig: { mode: 'flexible', preset: 'small' },
		verticalAlignment: 'top',
	},
	{
		id: 'source',
		fields: [
			{
				id: 'source-field',
				dataConfig: {
					type: 'field',
					field: { fieldName: 'generationSource', objectTable: 'statement' },
				},
				styleConfig: {
					size: 'xs',
					colorRule: { type: 'value-mapping' },
					valueMapping: [
						{
							value: 'MANUAL',
							displayText: 'Manual',
							color: { type: 'neutral' },
						},
						// PD-14044 adds run-generated sources; unmapped values
						// render raw, so new sources stay visible either way.
						{
							value: 'SCHEDULED',
							displayText: 'Scheduled',
							color: { type: 'info' },
						},
						{
							value: 'RUN_MANUAL',
							displayText: 'Manual Run',
							color: { type: 'neutral' },
						},
					],
				},
				displayStyle: 'badge',
			},
		],
		headers: [{ id: 'h-source', text: 'Source' }],
		minWidth: 90,
		sortable: false,
		alignment: 'left',
		widthConfig: { mode: 'flexible', preset: 'small' },
		verticalAlignment: 'top',
	},
	{
		id: 'distributed',
		fields: [
			{
				id: 'distributed-field',
				dataConfig: {
					type: 'field',
					field: { fieldName: 'distributedDate', objectTable: 'statement' },
				},
				styleConfig: {
					size: 'sm',
					colorRule: { type: 'theme', themeColor: 'textSecondary' },
					formatOptions: { dateFormat: 'short' },
				},
				displayStyle: 'date',
			},
		],
		headers: [{ id: 'h-distributed', text: 'Distributed' }],
		minWidth: 95,
		sortable: false,
		alignment: 'left',
		widthConfig: { mode: 'flexible', preset: 'small' },
		verticalAlignment: 'top',
	},
];

/**
 * Zones mobile card (PD-14059 treatment from day one): statement # leads,
 * Amount Due is the hero, the customer link is the wide block with As Of /
 * Period right-aligned beside it, source badge in the footer.
 */
const STATEMENT_MOBILE_CARD_CONFIG: MobileCardConfiguration = {
	cardLayout: 'zones',
	primaryFields: ['statement-number'],
	heroField: 'amount-due.amount-due-field',
	wideFields: ['client-info'],
	hiddenFields: ['ending-balance'],
};

export const STATEMENT_LIST_CONFIG: ListBuilderConfiguration = {
	lists: [
		{
			id: 'default-statements-all',
			cacheTTL: 300000,
			name: 'All Statements',
			type: 'default',
			active: true,
			sortOrder: 0,
			columns: createStatementColumns(),
			mobileCardConfig: STATEMENT_MOBILE_CARD_CONFIG,
			description: 'Stored customer statements, newest first',
			searchEnabled: false,
			clickAction: {
				type: 'navigate',
				path: '/statements/{id}',
			},
		},
	],
	paging: {
		defaultPageSize: 50,
		pageSizeOptions: [25, 50, 100],
	},
	filters: {
		activeFilters: [
			'statementClient',
			'statementCompany',
			'statementAsOfDateRange',
		],
	},
	objectType: 'statement',
};
